import React, { useState, useEffect } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { useGoogleMaps } from '../utils/GoogleMapsLoader';

const MapView = ({ discs, onMarkerClick, center, zoom = 3, visits, showJourney }) => {
    const [map, setMap] = useState(null);
    const [markers, setMarkers] = useState([]);
    
    const mapContainerStyle = {
        width: '100%',
        height: '600px'
    };

    // Default center (McCall, Idaho if no center provided)
    const defaultCenter = {
        lat: 44.9075,
        lng: -116.1014
    };

    // Shared loader (GoogleMapsLoader), not a separate useLoadScript call —
    // MapView previously loaded the Maps script independently from
    // VisitModal's loader, which meant any page rendering both (every disc
    // page) loaded the script twice. That's the root cause of the
    // "included multiple times" warning seen throughout dev testing, and —
    // more importantly — very likely the actual cause of the Lookup
    // InvalidValueError bug: two independently-loaded copies of the API can
    // leave classes like LatLng/PlacesService inconsistent with each other.
    const { isLoaded, loadError } = useGoogleMaps();

    // Clean up markers when component unmounts
    useEffect(() => {
        return () => {
            markers.forEach(marker => {
                if (marker.map) {
                    marker.map = null;
                }
            });
        };
    }, [markers]);

    // Create markers when map loads or data changes
    useEffect(() => {
        if (!map || !window.google) return;

        // Clear existing markers
        markers.forEach(marker => {
            if (marker.map) {
                marker.map = null;
            }
        });

        const newMarkers = [];

        if (showJourney && visits && visits.length > 0) {
            // Journey view - show numbered pins for each visit
            const bounds = new window.google.maps.LatLngBounds();
            let validLocations = 0;

            visits.forEach((visit, index) => {
                const position = {
                    lat: visit.location.lat,
                    lng: visit.location.lon || visit.location.lng
                };

                // Determine pin color based on position
                let backgroundColor;
                if (index === 0) {
                    backgroundColor = '#2196F3'; // Blue for start
                } else if (index === visits.length - 1) {
                    backgroundColor = '#43a047'; // Green for current
                } else {
                    backgroundColor = '#9E9E9E'; // Grey for interim
                }

                // Create pin element
                const pin = document.createElement('div');
                pin.className = 'custom-marker-pin';
                pin.style.cssText = `
                    width: 40px;
                    height: 40px;
                    background-color: ${backgroundColor};
                    border: 3px solid white;
                    border-radius: 50% 50% 50% 0;
                    transform: rotate(-45deg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    cursor: pointer;
                `;

                const label = document.createElement('div');
                label.textContent = (index + 1).toString();
                label.style.cssText = `
                    transform: rotate(45deg);
                    color: white;
                    font-weight: bold;
                    font-size: 16px;
                `;
                pin.appendChild(label);

                // Create advanced marker
                const marker = new window.google.maps.marker.AdvancedMarkerElement({
                    map: map,
                    position: position,
                    content: pin,
                    title: visit.location.courseName
                });

                bounds.extend(position);
                validLocations++;
                newMarkers.push(marker);
            });

            // Draw polyline connecting visits
            if (visits.length > 1) {
                const path = visits.map(v => ({
                    lat: v.location.lat,
                    lng: v.location.lon || v.location.lng
                }));

                new window.google.maps.Polyline({
                    path: path,
                    geodesic: true,
                    strokeColor: '#43a047',
                    strokeOpacity: 0.8,
                    strokeWeight: 3,
                    map: map
                });
            }

            // Fit map to bounds with padding
            if (validLocations > 0) {
                // Use setTimeout to ensure map is ready for fitBounds
                setTimeout(() => {
                    if (validLocations === 1) {
                        // For single location, center with reasonable zoom
                        map.setCenter(bounds.getCenter());
                        map.setZoom(12);
                    } else {
                        map.fitBounds(bounds, {
                            padding: { top: 50, right: 50, bottom: 50, left: 50 }
                        });
                    }
                }, 100);
            }

        } else if (discs && discs.length > 0) {
            // Homepage view - show all discs
            const bounds = new window.google.maps.LatLngBounds();
            let validDiscs = 0;
            
            discs.forEach((disc) => {
                if (!disc.currentLocation) return;

                const position = {
                    lat: disc.currentLocation.lat,
                    lng: disc.currentLocation.lng || disc.currentLocation.lon
                };

                // Create simple green pin for homepage
                const pin = document.createElement('div');
                pin.className = 'custom-marker-pin';
                pin.style.cssText = `
                    width: 35px;
                    height: 35px;
                    background-color: #43a047;
                    border: 3px solid white;
                    border-radius: 50% 50% 50% 0;
                    transform: rotate(-45deg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    cursor: pointer;
                `;

                const label = document.createElement('div');
                // Convert JD013 to just "13" for display
                const discNumber = parseInt(disc.discId.replace('JD', ''), 10).toString();
                label.textContent = discNumber;
                label.style.cssText = `
                    transform: rotate(45deg);
                    color: white;
                    font-weight: bold;
                    font-size: 12px;
                `;
                pin.appendChild(label);

                const marker = new window.google.maps.marker.AdvancedMarkerElement({
                    map: map,
                    position: position,
                    content: pin,
                    title: `${disc.discId} - ${disc.nickname || 'Jeffrey Disc'}`
                });

                // Add click listener
                if (onMarkerClick) {
                    pin.addEventListener('click', () => {
                        onMarkerClick(disc);
                    });
                }

                bounds.extend(position);
                validDiscs++;
                newMarkers.push(marker);
            });

            // Fit map to show all discs
            if (validDiscs > 0) {
                // Use setTimeout to ensure map is ready for fitBounds
                setTimeout(() => {
                    if (validDiscs === 1) {
                        // For single disc, center on it with reasonable zoom
                        map.setCenter(bounds.getCenter());
                        map.setZoom(8);
                    } else {
                        // For multiple discs, fit bounds to show all
                        // Generous padding for mobile-friendly display
                        map.fitBounds(bounds, {
                            padding: { top: 150, right: 150, bottom: 150, left: 150 }
                        });
                    }
                }, 200);
            }
        }

        setMarkers(newMarkers);
    }, [map, discs, visits, showJourney, onMarkerClick]);

    const handleMapLoad = (mapInstance) => {
        setMap(mapInstance);
    };

    // Handle loading and error states
    if (loadError) {
        return <div>Error loading maps</div>;
    }

    if (!isLoaded) {
        return <div>Loading maps...</div>;
    }

    return (
        <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center || defaultCenter}
            onLoad={handleMapLoad}
            options={{
                mapId: 'DEMO_MAP_ID' // Required for AdvancedMarkerElement
            }}
        >
        </GoogleMap>
    );
};

export default MapView;